import { useLayoutEffect, useRef, useState } from 'react'

import type { InheritanceStepCriteria, ProcessAnalysisConfig } from '../Processes/types'
import type { StepCriteriaRow } from './types'

function withRowClientIds(rows: InheritanceStepCriteria[]): StepCriteriaRow[] {
  return rows.map((r) => ({ ...r, rowClientId: crypto.randomUUID() }))
}

export function useProcessFormStepMatchingState(editedData: ProcessAnalysisConfig | null | undefined) {
  const [stepsInheritingIdFromPrevWindow, setStepsInheritingIdFromPrevWindow] = useState<
    StepCriteriaRow[]
  >([])
  const [stepsInheritingIdFromNextWindow, setStepsInheritingIdFromNextWindow] = useState<
    StepCriteriaRow[]
  >([])
  const [stepsInheritingIdWhenInMultiSystemFlow, setStepsInheritingIdWhenInMultiSystemFlow] =
    useState<StepCriteriaRow[]>([])

  const [rowIdsPendingFirstApply, setRowIdsPendingFirstApply] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  const processEditKey = editedData?.id ?? 'new'

  const editedDataRef = useRef(editedData)
  editedDataRef.current = editedData

  useLayoutEffect(() => {
    const data = editedDataRef.current
    if (!data) {
      setStepsInheritingIdFromPrevWindow([])
      setStepsInheritingIdFromNextWindow([])
      setStepsInheritingIdWhenInMultiSystemFlow([])
    } else {
      setStepsInheritingIdFromPrevWindow(
        withRowClientIds(data.steps_inheriting_id_from_prev_window ?? []),
      )
      setStepsInheritingIdFromNextWindow(
        withRowClientIds(data.steps_inheriting_id_from_next_window ?? []),
      )
      setStepsInheritingIdWhenInMultiSystemFlow(
        data.steps_inheriting_id_when_in_multi_system_flow.map((s) => ({
          ...s,
          max_step_distance: 0,
          max_time_sec_distance: 0,
          rowClientId: crypto.randomUUID(),
        })),
      )
    }
    setRowIdsPendingFirstApply(new Set())
  }, [processEditKey])

  const onRegisterNewRowNeedsApply = (rowClientId: string) => {
    setRowIdsPendingFirstApply((prev) => new Set(prev).add(rowClientId))
  }

  const onRowRuleApplied = (rowClientId: string) => {
    setRowIdsPendingFirstApply((prev) => {
      const next = new Set(prev)
      next.delete(rowClientId)
      return next
    })
  }

  const onRowRuleRemoved = (rowClientId: string) => {
    setRowIdsPendingFirstApply((prev) => {
      const next = new Set(prev)
      next.delete(rowClientId)
      return next
    })
  }

  return {
    stepsInheritingIdFromPrevWindow,
    setStepsInheritingIdFromPrevWindow,
    stepsInheritingIdFromNextWindow,
    setStepsInheritingIdFromNextWindow,
    stepsInheritingIdWhenInMultiSystemFlow,
    setStepsInheritingIdWhenInMultiSystemFlow,
    rowIdsPendingFirstApply,
    onRegisterNewRowNeedsApply,
    onRowRuleApplied,
    onRowRuleRemoved,
  }
}
